import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { CloudUpload } from 'lucide-react';
import { RouteParams } from '../library/types';
import { RootState } from '../../reducers';
import { Card, CardContent } from '../../components/ui/card';
import { Skeleton } from '../../components/ui/skeleton';
import { Progress } from '../../components/ui/progress';
import { TableCell, TableRow } from '../../components/ui/table';

interface PendingFilesProps {
  view?: 'grid' | 'list';
}

const GridViewItem = ({
  progress,
  fileName,
}: {
  progress: number;
  fileName: string;
}) => (
  <>
    <style>{`
      @keyframes float {
        0%, 100% { transform: translateY(0); }
        50% { transform: translateY(-5px); }
      }
      @keyframes expandIn {
        0% {
          opacity: 0;
          transform: scale(0.95) translateY(-10px);
        }
        100% {
          opacity: 1;
          transform: scale(1) translateY(0);
        }
      }
    `}</style>
    <Card className="col-span-1 relative flex flex-col h-full border-0 shadow-none bg-transparent animate-[expandIn_0.3s_ease-out]">
      <div className="relative">
        <Skeleton className="w-full pt-[56.25%] rounded-lg" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="flex flex-col items-center gap-4">
            <CloudUpload className="h-12 w-12 text-muted-foreground opacity-60 animate-[float_2s_ease-in-out_infinite]" />
            <span className="text-xl font-semibold text-muted-foreground">
              {Math.round(progress)}%
            </span>
          </div>
        </div>
      </div>
      <CardContent className="flex-1 flex flex-col justify-start py-4 px-0">
        <div className="flex items-start gap-3">
          <div className="h-8 w-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <CloudUpload className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="flex-1 overflow-hidden">
            <p className="font-semibold text-foreground line-clamp-2 mb-2">
              {fileName}
            </p>
            <Progress value={progress >= 99 ? 100 : progress} className="h-1" />
          </div>
        </div>
      </CardContent>
    </Card>
  </>
);

const ListViewItem = ({
  fileId,
  progress,
  fileName,
  isInRecentsView,
}: {
  fileId: string;
  progress: number;
  fileName: string;
  isInRecentsView?: boolean;
}) => (
  <TableRow
    key={fileId}
    className="group transition-all duration-200 opacity-60 hover:opacity-80"
  >
    <TableCell className="h-14 w-[45%] min-w-[250px] pl-4">
      <div className="flex items-center gap-3">
        <div className="h-8 w-8 rounded bg-blue-500/10 flex-shrink-0 flex items-center justify-center">
          <CloudUpload className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400 animate-pulse" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <p
              className="text-sm font-medium text-foreground truncate flex-1"
              title={fileName}
            >
              {fileName}
            </p>
            <span className="text-xs text-muted-foreground font-medium tabular-nums">
              {Math.round(progress)}%
            </span>
          </div>
          <Progress
            value={progress >= 99 ? 100 : progress}
            className="h-1 w-full bg-muted [&>div]:bg-blue-600 [&>div]:dark:bg-blue-400 [&>div]:transition-all [&>div]:duration-300"
          />
        </div>
      </div>
    </TableCell>
    <TableCell className="text-center w-[70px]">
      <span className="text-xs text-muted-foreground">Uploading</span>
    </TableCell>
    {isInRecentsView && (
      <TableCell className="hidden sm:table-cell w-[100px] text-center">
        <span className="text-xs text-muted-foreground">—</span>
      </TableCell>
    )}
    <TableCell className="hidden md:table-cell w-[80px] text-center">
      <span className="text-xs text-muted-foreground">—</span>
    </TableCell>
    <TableCell className="w-[60px] text-center">
      <span className="text-xs text-muted-foreground">—</span>
    </TableCell>
    <TableCell className="w-[44px]" />
  </TableRow>
);

const PendingFiles = ({ view = 'list' }: PendingFilesProps) => {
  const { upload } = useSelector((state: RootState) => state);
  const { folderId } = useParams<RouteParams>();
  const { activeUploads } = upload;
  const isInRecentsView = folderId === 'recents';

  const filteredFiles = activeUploads.filter((file) => {
    if (
      folderId === 'recents' ||
      folderId === 'private' ||
      folderId === 'favorites'
    )
      return true;
    return file.folderId === folderId;
  });

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (filteredFiles.length > 0) {
        event.preventDefault();
      }
      return undefined;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [filteredFiles]);

  if (filteredFiles.length === 0) return null;

  if (view === 'grid') {
    return (
      <>
        <style>{`
          @keyframes fadeInGroup {
            0% {
              opacity: 0;
            }
            100% {
              opacity: 1;
            }
          }
          .pending-uploads-group {
            animation: fadeInGroup 0.2s ease-out;
          }
        `}</style>
        {filteredFiles.map(({ fileId, progress, fileName }) => (
          <GridViewItem key={fileId} progress={progress} fileName={fileName} />
        ))}
      </>
    );
  }

  return (
    <>
      <style>{`
        @keyframes fadeInList {
          0% {
            opacity: 0;
          }
          100% {
            opacity: 1;
          }
        }
        .pending-list-group {
          animation: fadeInList 0.2s ease-out;
        }
      `}</style>
      {filteredFiles.map(({ fileId, progress, fileName }) => (
        <ListViewItem
          key={fileId}
          fileId={fileId}
          progress={progress}
          fileName={fileName}
          isInRecentsView={isInRecentsView}
        />
      ))}
    </>
  );
};

export default PendingFiles;
